/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.16.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Button, LinkBlock } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { TableFilterPresetQueryId } from '../../../components/Table/types/tableTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { stringFormatParamsHtml } from '../../../utils/stringFormat';
import PageLocale from '../../internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../internationalization/constants';
import {
    makeSelectPersonalDataConfirmTranslation,
    makeSelectTranslationCommon,
} from '../../internationalization/intSelectors';
import { ICommonTranslation, IPersonalDataConfirmTranslation } from '../../internationalization/types';
import { Loader } from '../../loader/components/Loader';
import Modal from '../../modal/components/Modal';
import { IModalRenderProps } from '../../modal/types/modalTypes';
import { PersonalDataConfirmModalStyled } from './styled/PersonalDataConfirmModalStyled';

interface IPersonalDataConfirmModal extends Pick<IModalRenderProps, 'onCloseModal'> {
    accountsNotConfirmed: boolean;
}

const PersonalDataConfirmModal = ({
                                      onCloseModal,
                                      translationCommon,
                                      translationPersonalDataConfirm,
                                  }: IPersonalDataConfirmModal & IConnectedProps) => {

    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(!translationPersonalDataConfirm.title);
    }, [translationPersonalDataConfirm]);

    const tText = useMemo(() => translationPersonalDataConfirm.text.onlySubscribers,
        [translationPersonalDataConfirm]);

    const tStep1 = useMemo(() => translationPersonalDataConfirm.step[1].onlySubscribers,
        [translationPersonalDataConfirm]);

    const renderSubscribersLink = useCallback((text: string, dataName: string) =>
        <LinkBlock
            dataName={dataName}
            url={`${RoutePath.subscriberMobile}?${TableFilterPresetQueryId}=personal_data_not_confirmed`}
        >
            {text}
        </LinkBlock>, []);

    return (
        <Modal dataName="personal-data-confirm-modal" style={{ paddingBottom: 32 }} width="720px">
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.personalDataConfirm]}/>
            <Loader showLoader={isLoading}>
                <PersonalDataConfirmModalStyled>
                    <div className="confirm-modal__error">
                        <ImgStyled
                            className="confirm-modal__error-img"
                            height="100px"
                            src={icons.infoInvertErrorIcon}
                            width="100px"
                        />
                        {translationPersonalDataConfirm.title}
                    </div>
                    <div className="confirm-modal__text">
                        {
                            stringFormatParamsHtml(tText, {
                                'subscribers': renderSubscribersLink(
                                    translationPersonalDataConfirm.text.link.subscribers,
                                    'personal-data-confirm-link-subscriber',
                                ),
                            })
                        }
                    </div>
                    <div className="confirm-modal__subtitle">
                        {translationPersonalDataConfirm.subtitle}
                    </div>
                    <div className="confirm-modal__step-list">
                        <div className="confirm-modal__step">
                            <div className="confirm-modal__step-count">1</div>
                            <div>
                                {
                                    stringFormatParamsHtml(tStep1, {
                                        'subscribers': renderSubscribersLink(
                                            translationPersonalDataConfirm.step[1].link.subscribers,
                                            'personal-data-confirm-step1-link-subscriber',
                                        ),
                                    })
                                }
                            </div>
                        </div>
                        <div className="confirm-modal__step">
                            <div className="confirm-modal__step-count">2</div>
                            <div dangerouslySetInnerHTML={{ __html: translationPersonalDataConfirm.step[2].all }}/>
                        </div>
                        <div className="confirm-modal__step">
                            <div className="confirm-modal__step-count">3</div>
                            <div dangerouslySetInnerHTML={{ __html: translationPersonalDataConfirm.step[3].all }}/>
                        </div>
                    </div>
                    <Modal.Control center>
                        <Button
                            dataName="personal-data-confirm-modal-close"
                            kind="mediumOutline"
                            onClick={onCloseModal}
                        >
                            {translationCommon.modal.accessibly}
                        </Button>
                    </Modal.Control>
                </PersonalDataConfirmModalStyled>
            </Loader>
        </Modal>
    );
};

interface IConnectedProps {
    translationCommon: ICommonTranslation;
    translationPersonalDataConfirm: IPersonalDataConfirmTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translationCommon: makeSelectTranslationCommon(),
    translationPersonalDataConfirm: makeSelectPersonalDataConfirmTranslation(),
});

export default connect(mapStateToProps)(PersonalDataConfirmModal);
